/**
 * What a student is shown of a diagnosis — one place, so DiagnosisBlock and
 * FirstIssueBlock can never word it differently.
 *
 * Both vocabularies are read (docs/FeatureDesigns/diagnosis/accuracy/
 * diagnosis-structure-target.md). Three rules decide what is shown:
 *   - `none` and `undetermined` show nothing: the first has nothing to say,
 *     the second could not tell what went wrong;
 *   - a `tentative` diagnosis is not stated as fact, so it is not shown —
 *     unless a teacher approved it, when the server sends it without its
 *     doubt (`approvedAsStated`, functions selection/withhold.ts);
 *   - `misapplied_rule` (vocabulary 1) is deliberately not called a gap: the
 *     student knows the rule, what they are missing is where it stops applying.
 */
import type { Diagnosis } from '@padcanvas/shared';

const KIND_LABEL: Partial<Record<Diagnosis['kind'], string>> = {
    slip: 'Careless slip',
    wrong_knowledge: 'Misconception',
    missing_knowledge: 'Concept gap',
    improvement: 'Could be better',
    misapplied_rule: 'Right idea, wrong place',
    gap: 'Concept gap',
};

export interface StudentDiagnosis {
    readonly kind: Diagnosis['kind'];
    readonly label: string;
    readonly cause: string;
}

/** The diagnosis as a student may see it, or undefined when nothing is to be shown. */
export function diagnosisForStudent(diagnosis: Diagnosis | undefined): StudentDiagnosis | undefined {
    if (!diagnosis || diagnosis.certainty === 'tentative') return undefined;
    const label = KIND_LABEL[diagnosis.kind];
    const cause = diagnosis.cause?.trim() ?? '';
    if (!label || !cause) return undefined;
    return { kind: diagnosis.kind, label, cause };
}

/** The diagnosis as a teacher reviewing it may see it: a tentative cause too,
 *  flagged, since review exists for the ones the system is unsure of. Never
 *  for a student. */
export function diagnosisForReview(diagnosis: Diagnosis | undefined): (StudentDiagnosis & { tentative: boolean }) | undefined {
    if (!diagnosis) return undefined;
    const shown = diagnosisForStudent({ ...diagnosis, certainty: undefined });
    return shown && { ...shown, tentative: diagnosis.certainty === 'tentative' };
}

/** The plain label for a diagnosis kind, or undefined for one with none
 *  (`none`, `undetermined`). For a reader comparing two readings' kinds. */
export function diagnosisKindLabel(kind: string | undefined): string | undefined {
    return kind ? KIND_LABEL[kind as Diagnosis['kind']] : undefined;
}
